import React, { useMemo, useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import WarningAmber from "@mui/icons-material/WarningAmber";
import Add from "@mui/icons-material/Add";
import { useChatbotSlots } from "../../hooks/useChatbot";

export default function ChatbotTreeView({ userId, onSelectSlot, selectedSlotId, onAddChild }) {
  const { data: slots = [], isLoading } = useChatbotSlots(userId);
  const [openNodes, setOpenNodes] = useState({});

  const { roots, childrenByParent } = useMemo(() => {
    const byParent = new Map();
    (slots || []).forEach((s) => {
      const pid = s.id_pai != null ? String(s.id_pai) : "0";
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(s);
    });
    const roots = byParent.get("0") || [];
    return { roots, childrenByParent: byParent };
  }, [slots]);

  const toggleNode = (id) => {
    setOpenNodes((prev) => {
      const current = prev[id] ?? true; // padrão: aberto
      return { ...prev, [id]: !current };
    });
  };

  const renderNode = (slot, level = 0) => {
    const id = String(slot.id);
    const children = childrenByParent.get(id) || [];
    const hasChildren = children.length > 0;
    const isOpen = openNodes[id] ?? true;
    const isSelected = String(selectedSlotId || "") === id;
    const isExcecao = slot.identificador === "Exceção";
    const isRoot = slot.id_pai === 0;

    return (
      <Box key={id}>
        <ListItemButton
          dense
          selected={isSelected}
          onClick={() => onSelectSlot?.(slot)}
          sx={{
            pl: 1 + level * 2.4,
            pr: 1,
            borderLeft: level > 0 ? "1px solid" : "none",
            borderColor: "divider",
            borderRadius: 1,
            mb: 0.25,
            "&.Mui-selected": {
              bgcolor: "primary.light",
              "&:hover": { bgcolor: "primary.light" },
            },
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(id);
              }}
              sx={{ mr: 1 }}
            >
              {isOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 32, mr: 0.5 }} />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
            {isExcecao ? (
              <WarningAmber fontSize="small" color="warning" sx={{ flexShrink: 0 }} />
            ) : (
              <ChatBubbleOutline fontSize="small" color={isRoot ? "primary" : "action"} sx={{ flexShrink: 0 }} />
            )}
            <ListItemText
              primary={
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    fontWeight: isRoot ? 600 : 500,
                    color: isExcecao ? "warning.dark" : "text.primary",
                  }}
                  title={slot.identificador || slot.nome || `Passo ${slot.id}`}
                >
                  {slot.identificador || slot.nome || `Passo ${slot.id}`}
                </Typography>
              }
              secondaryTypographyProps={{ component: "div" }}
              secondary={
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                  {slot.global ? (
                    <Chip
                      size="small"
                      label="Global"
                      color="warning"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  ) : null}
                  {slot.ignorarglobal ? (
                    <Chip
                      size="small"
                      label="Ignorar globais"
                      color="default"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  ) : null}
                  {isExcecao && (
                    <Chip
                      size="small"
                      label="Exceção"
                      color="warning"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  )}
                </Box>
              }
              sx={{ mr: 1 }}
            />
          </Box>
          <Tooltip title="Adicionar subpasso">
            <span>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddChild?.(slot);
                }}
                disabled={isExcecao}
              >
                <Add fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {children.map((child) => renderNode(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  if (!userId) return null;

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Carregando árvore…
        </Typography>
      </Box>
    );
  }

  if (!slots.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum passo encontrado. Inicialize o fluxo no modo Canvas.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxHeight: "100%",
        overflow: "auto",
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, letterSpacing: 0.8 }}>
        Estrutura em árvore
      </Typography>
      <List dense disablePadding>
        {roots.map((slot) => renderNode(slot, 0))}
      </List>
    </Box>
  );
}

